/*
 * Copyright (c) 2026.
 * Spin in het Web B.V. Apeldoorn.
 * Deze software mag alleen gebruikt / bekeken / gewijzigd worden volgens de voorwaarden die zijn vastgelegd in een bijbehorende schriftelijke overeenkomst met Spin in het Web B.V.
 */

/**
 * Auteur: Jelmer Jellema - Spin in het Web B.V
 * Gegenereerd met Claude Code (AI), 30-06-2026
 *
 * Tussenklasse voor node-menu-componenten. Erft van BaseNodeGekoppeldComponent
 * (node/cy inputs, geselecteerd signal) en voegt de gedeelde menu-logica toe:
 * toegang tot de modelcanvas-state (effectiefModelleerniveau, condtag) en
 * delegatiemethoden voor menu-acties (changeAction, initAddAction, submenu-state).
 */
import {Directive, ElementRef, effect, inject, signal} from '@angular/core';
import type {NodeSingular} from 'cytoscape';
import {BaseNodeGekoppeldComponent} from './base-node-gekoppeld.component';
import {DlModelCanvasComponent} from '../canvas/dl-modelcanvas/dl-modelcanvas.component';

@Directive()
export abstract class BaseNodeMenuComponent extends BaseNodeGekoppeldComponent {

    /**
     * Het canvas waarbinnen dit menu-component leeft. Levert globale state
     * (effectiefModelleerniveau, condtag) en voert de model-acties uit via de samenwerk-baas.
     */
    protected readonly modelcanvas = inject(DlModelCanvasComponent);

    /**
     * Naam van het momenteel openstaande submenu, of null als geen submenu open is.
     */
    protected readonly actievSubmenu = signal<string | null>(null);

    /**
     * Samengestelde sleutel van de initAddAction die momenteel actief is, opgebouwd
     * uit type en (optioneel) element, gescheiden door ':'. Null als er geen actieve
     * add-actie is. Wordt lokaal bijgehouden: gezet bij uitvoerInitAddAction, gereset
     * zodra het canvas meldt dat inAddRelation false is.
     */
    private readonly _actiefInitAddActieType = signal<string | null>(null);

    /**
     * Het eigen host-element, om bij afbraak te kunnen bepalen of de nog openstaande menu-uitleg van
     * dit menu is.
     */
    private readonly host: HTMLElement = inject(ElementRef).nativeElement;

    constructor() {
        super();
        // Reset het actieve type zodra de add-relatie-modus eindigt.
        effect(() => {
            if (!this.modelcanvas.inAddRelation()) {
                this._actiefInitAddActieType.set(null);
            }
        });
    }

    /**
     * Trekt de menu-uitleg in als die nog van dit menu is: bij afbraak komt er geen pointerleave meer.
     */
    override ngOnDestroy(): void {
        this.modelcanvas.wisMenuUitleg(this.host);
        super.ngOnDestroy();
    }

    /**
     * Mag er een menu op deze node open? Een menu is er om het model te wijzigen, dus in readonly
     * hoort het er niet te zijn — of die readonly nu uit replay, een verbroken verbinding of straks
     * meekijken komt. De AngularJS-versie deed hetzelfde in het `show`-event van de qtip
     * (`cytocanvas.modelview.controller.js` op `main`, r. 3699); bij de verhuizing naar deze
     * componenten is die guard blijven liggen (bevinding a6.15, 02-09-2026).
     *
     * Ook weg zodra het naamveld van de node is geopend: het menu komt terug bij de volgende selectie.
     * En weg bij een node die tijdens het leggen van een relatie wordt aangeklikt als tweede argument.
     */
    protected magMenuTonen(): boolean {
        return this.geselecteerd() && !this.modelcanvas.fullReadonly() && !this.modelcanvas.isLabelBewerkt(this.node)
            && this.modelcanvas.magNodeMenuTonen(this.node);
    }

    /**
     * Het effectief berekende modelleerniveau (0/20/30/40/50). Leest live via
     * het canvas uit `DlModel`. Templates gebruiken dit voor @if-checks op
     * data-minniveau.
     */
    protected get effectiefModelleerniveau(): number {
        return this.modelcanvas.getEffectiefModelleerniveau();
    }

    /**
     * De huidige condtag-state, of null als er geen actieve condtag is.
     * Leest live via het canvas uit de `CondtagService`. Templates gebruiken dit
     * voor @if-checks op data-condstate-cond/cons/match.
     */
    protected get condtag(): {tag: string; state: 'cond' | 'cons'} | null {
        return this.modelcanvas.getCondtag();
    }

    /**
     * Voert een changeAction uit voor de gekoppelde node: vervangt het type
     * of past een eigenschap aan. Loopt via het canvas naar de samenwerk-baas.
     */
    protected uitvoerChangeAction(actiedata: Record<string, unknown>): void {
        this.modelcanvas.uitvoerChangeAction(this.node as NodeSingular, actiedata);
    }

    /**
     * Initieert een add-actie voor de gekoppelde node: start het toevoegen van
     * een nieuwe relatie of subelement. Loopt via het canvas en slaat het
     * actie-type lokaal op voor visuele feedback (selectedMenuItem).
     */
    protected uitvoerInitAddAction(actiedata: Record<string, unknown>): void {
        this.modelcanvas.uitvoerInitAddAction(this.node as NodeSingular, actiedata);
        const type = actiedata['type'] as string ?? null;
        const element = actiedata['element'] as string | undefined;
        this._actiefInitAddActieType.set(type ? (element ? `${type}:${element}` : type) : null);
    }

    /**
     * Geeft aan of de opgegeven initAddAction momenteel actief is, d.w.z. de gebruiker
     * heeft op die knop geklikt en wacht op een doel-node. Het optionele element-argument
     * wordt gebruikt wanneer twee knoppen hetzelfde type maar een ander element hebben
     * (bijv. quantity_space_point vs quantity_space_interval).
     */
    protected isActiefInitAddActie(type: string, element?: string): boolean {
        const sleutel = element ? `${type}:${element}` : type;
        return this.modelcanvas.inAddRelation() && this._actiefInitAddActieType() === sleutel;
    }

    /**
     * Meldt aan het canvas boven welk menu-item de muis staat, zodat de uitleg daarbij getoond kan
     * worden. Het anker is de menulijst waar het item in zit: hoofdmenu of submenu, net als in de
     * ng1-versie. Items zonder `data-uitleg` (submenu-openers, ballon) trekken de melding juist in.
     */
    protected opItemHover(gebeurtenis: PointerEvent): void {
        if (!this.modelcanvas.isMenuUitlegAan()) {
            return;
        }
        const item = (gebeurtenis.target as HTMLElement).closest('[data-uitleg]');
        const anker = item?.closest('ul');
        if (item && anker) {
            this.modelcanvas.meldMenuUitleg(item.getAttribute('data-uitleg') ?? '', anker);
        } else {
            this.modelcanvas.wisMenuUitleg(this.host);
        }
    }

    /**
     * Trekt de menu-uitleg in zodra de muis het menu verlaat.
     */
    protected opMenuVerlaten(): void {
        this.modelcanvas.wisMenuUitleg(this.host);
    }

    /**
     * Opent het opgegeven submenu, of sluit het als het al open was (toggle).
     */
    protected toggleSubmenu(naam: string): void {
        this.actievSubmenu.update(huidig => huidig === naam ? null : naam);
    }

    /**
     * Wisselt de argumenten van de gekoppelde relatie-node om.
     */
    protected uitvoerSwitchArgs(): void {
        this.modelcanvas.uitvoerSwitchArgs(this.node as NodeSingular);
    }

    /**
     * Opent de ballon-editor voor de gekoppelde node.
     */
    protected uitvoerEditBallon(): void {
        this.modelcanvas.uitvoerEditBallon(this.node as NodeSingular);
    }

    /**
     * Schakelt de allewaarden-visualisatie aan of uit voor de gekoppelde quantity-node.
     */
    protected uitvoerToggleAllewaarden(): void {
        this.modelcanvas.uitvoerToggleAllewaarden(this.node as NodeSingular);
    }

    /**
     * Schakelt de derivative-waarde aan of uit voor de gekoppelde node.
     */
    protected uitvoerToggleDValue(): void {
        this.modelcanvas.uitvoerToggleDValue(this.node as NodeSingular);
    }

    /**
     * Schakelt een quantity-space-waarde aan of uit voor de gekoppelde node.
     */
    protected uitvoerToggleQValue(): void {
        this.modelcanvas.uitvoerToggleQValue(this.node as NodeSingular);
    }

    /**
     * Voegt een nieuw quantity-space-element toe boven of onder de ruimte.
     */
    protected uitvoerNewQuantitySpaceElement(positie: 'top' | 'bottom'): void {
        this.modelcanvas.uitvoerNewQuantitySpaceElement(this.node as NodeSingular, positie);
    }

    /**
     * Zet of verwijdert de zero-markering van het gekoppelde quantity-space-punt.
     */
    protected uitvoerSetQsPointZero(): void {
        this.modelcanvas.uitvoerSetQsPointZero(this.node as NodeSingular);
    }

    /**
     * Schakelt een exogeen-type aan of uit voor de gekoppelde quantity-node.
     */
    protected uitvoerToggleExogeen(exoType: string): void {
        this.modelcanvas.uitvoerToggleExogeen(this.node as NodeSingular, exoType);
    }
}
